import { PLAYER, radiusToMass } from '../constants';
import { Snake, type SnakeSkin } from '../entities/Snake';
import type { ServerPlayer } from '../../net/serverWorld';

/**
 * Phase-2-Full Session 5 — client-side prediction for the local player.
 *
 * The problem we're solving:
 *
 *   Session 4 put the server in charge of every snake's position.
 *   Each frame, the local player's snake renders from the LAST patch
 *   the server sent — which is 50-150 ms old by the time it arrives.
 *   When the player moves the mouse, the on-screen snake reacts that
 *   round-trip later. Even at 50 ms RTT the lag is perceptible; at
 *   200 ms it's miserable.
 *
 * The solution:
 *
 *   Run the EXACT same physics the server runs, locally, against the
 *   local player's input. The on-screen snake is the LOCALLY-PREDICTED
 *   state, applied each frame from current mouseWorld + boost. The
 *   player sees instant response.
 *
 *   But local prediction will drift from server truth over time (input
 *   timing differences, server-side bot AI affecting collisions, etc).
 *   Each Colyseus patch (~20 Hz) we run `reconcile()`: gently pull the
 *   prediction toward server position with exponential blending. Mass
 *   is hard-set from server (eating is server-authoritative — we'd
 *   never know to credit ourselves for an eat without the message).
 *
 *   Large divergences (respawn, kill) trigger a snap reset rather
 *   than blending — the player perceives those as discrete events
 *   anyway, so a step transition reads cleanly.
 *
 * Internally we reuse the existing client `Snake` class — its update()
 * was ported into `server/src/sim/ServerSnake.ts` in Session 1, so the
 * two share their math. As long as we keep the constants synced
 * (server/src/sim/constants.ts ↔ src/game/constants.ts), prediction
 * and authority will agree on idle input.
 */

const SNAKE_ID_LOCAL = -1;

/** Reconciliation tuning. Picked empirically; revisit if drift feels
 *  rubbery or laggy. */
const RECONCILE = {
  /** Blend factor per patch — how much of the gap we close per arrive.
   *  0.18 = ~85% closed in 10 patches (~500 ms). High enough to keep
   *  drift bounded, low enough not to wobble the snake visually. */
  BLEND_FACTOR: 0.18,
  /** Above this head displacement, we SNAP rather than blend.
   *  Catches respawn / kill / teleport cases where blending would
   *  leave a visible rubber band. */
  SNAP_DISTANCE: 250,
  /** Above this displacement we just hard-set every segment to the
   *  server's chain (full chain reset, not just head). Below it we
   *  let chain-follow physics pull the body to the snapped head. */
  CHAIN_RESET_DISTANCE: 600,
} as const;

/** Build the canonical player skin — same set used by World.makePlayerSkin. */
function makePlayerSkin(hue: number): SnakeSkin {
  return {
    hue, sat: 100, baseLight: 56, lightAmp: 28,
    waveFreq: 5.5, waveSpeed: 0.55, wavePhase: 0,
    darkBandDepth: 0, darkBandFreq: 0, darkBandSpeed: 0, darkBandPhase: 0,
    orbCount: 5, orbSpeed: 0.18,
  };
}

export class LocalPrediction {
  /** The simulated Snake — same class the renderer was built for, so
   *  we get tongue flicks, chomp, digesting orbs, leader aura, the
   *  whole visual stack for free. */
  readonly snake: Snake;

  /** True once we've received at least one server patch for the local
   *  player. Before that, the snake is at its default spawn (center of
   *  map) — used as a placeholder until the first reconcile lands. */
  private bootstrapped = false;

  constructor(name: string, hue: number, sessionId: string) {
    this.snake = new Snake({
      id: SNAKE_ID_LOCAL,
      isPlayer: true,
      name: name || 'YOU',
      skin: makePlayerSkin(hue),
      spawn: { x: 2500, y: 2500 },
      heading: 0,
      mass: radiusToMass(PLAYER.START_RADIUS) + 80,
    });
    // Sessionid kept on the side so the renderer can match against
    // serverPlayers map without poking Snake's structure.
    this._sessionId = sessionId;
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  private _sessionId: string;
  get sessionId(): string { return this._sessionId; }

  /**
   * Apply the player's CURRENT input + step the physics by dt. Called
   * from the engine's update loop every fixed-dt tick.
   *
   * `mouseWorldX/Y` is the steer target (where the player wants to go),
   * `boost` is the held button. These are the same values we're already
   * sending to the server via `sendInputTick` — local prediction just
   * runs the same physics with them on the local snake.
   */
  step(dt: number, mouseWorldX: number, mouseWorldY: number, boost: boolean): void {
    if (!this.snake.alive) return;
    this.snake.steerTargetRaw.x = mouseWorldX;
    this.snake.steerTargetRaw.y = mouseWorldY;
    this.snake.boosting = boost;
    this.snake.update(dt);
  }

  /**
   * Pull predicted state toward server truth.
   *
   * Mass: server-authoritative (eating is server-side; we'd over- or
   *   under-credit ourselves locally). Hard-set every patch.
   * Alive: server-authoritative (kills happen server-side). Hard-set.
   * Position: blend toward server head with BLEND_FACTOR (smooth).
   *   Snap if the gap exceeds SNAP_DISTANCE (respawn / kill / lag).
   *   Reset full chain if it exceeds CHAIN_RESET_DISTANCE (truly
   *   discontinuous transitions like a teleport).
   *
   * Heading: blend toward server's, same factor — keeps eye direction
   *   honest.
   */
  /** Tracker so we only blend the position when the server actually
   *  delivered a new authoritative head — see comment in reconcile()
   *  below for the bug this fixes. */
  private lastServerHeadX = NaN;
  private lastServerHeadY = NaN;

  reconcile(server: ServerPlayer, engineTime: number = 0): void {
    if (!this.bootstrapped) {
      this.bootstrapped = true;
      this.snapToServer(server);
      // Mirror server invincibility into engine-time so the renderer's
      // flicker effect (Snake.isInvincible) fires for the LOCAL snake
      // during the spawn shield. Without this the player never sees a
      // visual confirmation that they're protected for 5 seconds and
      // assumes the protection isn't working.
      const remMs = server.invincibleUntil - Date.now();
      this.snake.invincibleUntil = remMs > 0 ? engineTime + remMs / 1000 : 0;
      this.lastServerHeadX = server.headX;
      this.lastServerHeadY = server.headY;
      return;
    }

    // Server is canon on lifecycle + mass.
    if (this.snake.alive !== server.alive) {
      if (server.alive) {
        // Server says we respawned — reset to spawn state.
        this.snapToServer(server);
        const remMs = server.invincibleUntil - Date.now();
        this.snake.invincibleUntil = remMs > 0 ? engineTime + remMs / 1000 : 0;
        this.lastServerHeadX = server.headX;
        this.lastServerHeadY = server.headY;
        return;
      } else {
        this.snake.alive = false;
      }
    }
    this.snake.mass = server.mass;
    // Keep invincibility in sync — the server can extend or end it
    // (e.g., shield-break collision) at any time. Hard-set is fine
    // and CHEAP; we only short-circuit the position blend below.
    const remMs2 = server.invincibleUntil - Date.now();
    this.snake.invincibleUntil = remMs2 > 0 ? engineTime + remMs2 / 1000 : 0;

    if (!this.snake.alive) return;

    // ── Patch-arrival gate for the position blend ──────────────
    //
    // The stutter audit caught a textbook bug here: reconcile() is
    // called from GameEngine.update EVERY FRAME (60+ Hz), but server
    // state patches arrive at ~20 Hz. Between patches `server.headX/Y`
    // is the SAME value from the last patch, so the blend below
    // pulled the predicted head TOWARD a stale ~50-100 ms old
    // position 3-4× per patch. At BLEND_FACTOR=0.18 that's roughly
    // 1 - (1-0.18)^4 ≈ 55% of the prediction's per-tick forward
    // progress undone, producing constant micro-stutter timed to
    // the patch rate. The mouse advances the head forward each frame
    // and reconcile yanks it backward → snake "fights the cursor".
    //
    // Gate: only blend when the server actually delivered a new
    // head position. Patch-rate-correct, calibrated for BLEND_FACTOR=0.18.
    if (server.headX === this.lastServerHeadX &&
        server.headY === this.lastServerHeadY) {
      return;
    }
    this.lastServerHeadX = server.headX;
    this.lastServerHeadY = server.headY;

    // Position blend / snap.
    const head = this.snake.segments[0]!;
    const dx = server.headX - head.x;
    const dy = server.headY - head.y;
    const d = Math.hypot(dx, dy);

    if (d > RECONCILE.CHAIN_RESET_DISTANCE) {
      this.snapToServer(server);
      return;
    }
    if (d > RECONCILE.SNAP_DISTANCE) {
      // Mid-range snap: shift the WHOLE chain by the head delta so
      // the body shape is preserved and chain-follow physics doesn't
      // have to play catch-up across hundreds of pixels of head-vs-
      // body gap. Heading is BLENDED, not hard-set, so we don't
      // discard the local prediction's mouse-tracking direction in
      // favor of a possibly stale (~60-100 ms old) server heading.
      for (let i = 0; i < this.snake.segments.length; i++) {
        const s = this.snake.segments[i]!;
        s.x += dx;
        s.y += dy;
      }
      const dh1 = ((server.heading - this.snake.heading + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
      this.snake.heading += dh1 * 0.5;
      return;
    }
    // Smooth blend toward server head + heading.
    const t = RECONCILE.BLEND_FACTOR;
    head.x += dx * t;
    head.y += dy * t;
    const dh = ((server.heading - this.snake.heading + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
    this.snake.heading += dh * t;
  }

  /**
   * Forward an eat event — the server tells us we ate. Trigger the
   * same visual flow World.processFoodEating used to: chomp, pulse on
   * perfect match, digesting orb on perfect match.
   *
   * Caller has already confirmed the event is for THIS snake (id
   * matches sessionId).
   */
  applyEat(hue: number, mult: number): void {
    this.snake.triggerChomp();
    if (mult >= 2.5) {
      this.snake.triggerPulse();
      this.snake.addDigestingOrb(hue);
    }
  }

  /**
   * Hard-reset every visible field to whatever the server says. Used
   * for first-bootstrap, respawn, and any divergence too large to
   * blend without rubber-banding.
   */
  private snapToServer(server: ServerPlayer): void {
    const s = this.snake;
    s.alive = server.alive;
    s.mass = server.mass;
    s.radius = server.radius;
    s.heading = server.heading;
    s.boosting = server.boosting;
    // Head + chain.
    if (s.segments.length === 0) return;
    s.segments[0]!.x = server.headX;
    s.segments[0]!.y = server.headY;
    // Lay the body straight back along the heading — chain physics
    // will reshape it as the head moves. Looks better than copying
    // server.segments (which is at the SERVER's interpolated state,
    // not our render time).
    const spacing = s.radius * 0.78;
    const dx = -Math.cos(server.heading);
    const dy = -Math.sin(server.heading);
    for (let i = 1; i < s.segments.length; i++) {
      s.segments[i]!.x = server.headX + dx * spacing * i;
      s.segments[i]!.y = server.headY + dy * spacing * i;
    }
    // PRESERVE forward speed across the snap. Setting currentSpeed=0
    // here made the snake render nearly stationary for ~100-300 ms
    // after every snap (bootstrap / respawn / lag-spike) while the
    // ACCEL_STIFFNESS damp ramp re-spooled speed via Snake.update.
    // Visible as "snake stops dead, then accelerates back to normal"
    // — exactly the user-reported intermittent stutter pattern when
    // it happens mid-game from a divergence > 250 px.
    //
    // We deliberately do NOT touch s.currentSpeed: if the snake was
    // moving at speed S pre-snap, it continues moving at speed S
    // post-snap from the new position. That's the visual continuity
    // the snap is trying to deliver — server moves the truth, the
    // local prediction keeps marching along its current trajectory.
  }
}
